(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory(
      require('./ai-chat-core.js'),
      require('./ai-chat-state.js'),
      null,
    );
    return;
  }

  if (root) {
    root.MarineMaxAIChatConversation = factory(
      root.MarineMaxAIChatCore,
      root.MarineMaxAIChatState,
      root,
    );
  }
})(typeof window !== 'undefined' ? window : null, function (core, state, root) {
  'use strict';

  if (!core) {
    throw new Error('MarineMax AI Chat core is required by the conversation module');
  }
  if (!state) {
    throw new Error('MarineMax AI Chat state is required by the conversation module');
  }

  const { messageFormats, messageTypes } = state;

  function createConversationController(options = {}) {
    const client = options.client;
    const messages = options.messages;
    const getMessageContext = options.getMessageContext;
    const onBeforeSend = options.onBeforeSend || (() => {});
    const onControlsDisabled = options.onControlsDisabled || (() => {});
    const typingDelayMs = options.typingDelayMs ?? core.constants.TYPING_DELAY;
    const delay =
      options.delay ||
      ((milliseconds) =>
        new Promise((resolve) => {
          const setTimer =
            (root && root.setTimeout && root.setTimeout.bind(root)) ||
            globalThis.setTimeout.bind(globalThis);
          setTimer(resolve, milliseconds);
        }));
    const TextDecoderClass =
      options.TextDecoder ||
      (root && root.TextDecoder) ||
      (typeof TextDecoder !== 'undefined' ? TextDecoder : null);
    const stockNumber = String(options.stockNumber || '');
    const storeCode = String(options.storeCode || '');
    let conversationId = null;
    let currentRequestId = 0;

    if (
      !client ||
      ['cancel', 'release', 'request'].some(
        (methodName) => typeof client[methodName] !== 'function',
      )
    ) {
      throw new Error('An AI Chat client is required by the conversation module');
    }
    if (
      !messages ||
      !messages.store ||
      ['append', 'getElement', 'remove', 'update'].some(
        (methodName) => typeof messages[methodName] !== 'function',
      )
    ) {
      throw new Error('An AI Chat messages controller is required by the conversation module');
    }
    if (!TextDecoderClass) {
      throw new Error('TextDecoder is required by the conversation module');
    }

    function isCurrentRequest(requestId) {
      return requestId === currentRequestId;
    }

    function cancel() {
      currentRequestId += 1;
      client.cancel();
      clearTypingMessages();
    }

    function setConversationId(nextConversationId) {
      conversationId = nextConversationId || null;
    }

    function clearTypingMessages() {
      messages.store
        .snapshot()
        .filter((message) => message.type === messageTypes.TYPING)
        .forEach((message) => messages.remove(message.id));
    }

    function createPayload(message) {
      const payload = {
        stock_number: stockNumber,
        store_code: storeCode,
        message_content: message,
        format: 'markdown',
      };
      if (conversationId) {
        payload.conversation_id = conversationId;
      }
      return payload;
    }

    async function throwForHttpError(response) {
      if (response.ok) return;

      let errorText = `HTTP ${response.status}`;
      try {
        const errorBody = await response.text();
        if (errorBody) errorText += `: ${errorBody}`;
      } catch (_) {}
      throw new Error(errorText);
    }

    async function send(message) {
      const messageContent = String(message || '');
      if (!messageContent.trim()) return;

      cancel();
      const requestId = currentRequestId;
      onBeforeSend(messageContent);

      messages.append(
        messageTypes.USER,
        messageContent,
        messageFormats.MARKDOWN,
      );
      onControlsDisabled(true);

      const typingMessage = messages.append(
        messageTypes.TYPING,
        '',
        messageFormats.TEXT,
      );

      try {
        const payload = createPayload(messageContent);
        if (typeof getMessageContext === 'function') {
          const messageContext = await getMessageContext();
          if (messageContext && typeof messageContext === 'object') {
            Object.assign(payload, messageContext);
          }
        }

        const response = await client.request(payload, {
          isCurrent: () => isCurrentRequest(requestId),
        });
        await throwForHttpError(response);

        if (!response.body) {
          throw new Error('No response body');
        }

        const assistantMessage = messages.append(
          messageTypes.ASSISTANT,
          '',
          messageFormats.MARKDOWN,
        );
        const reader = response.body.getReader();
        const decoder = new TextDecoderClass();
        let partial = '';
        let typingRemoved = false;

        while (true) {
          const { done, value } = await reader.read();
          if (!isCurrentRequest(requestId)) return;
          if (done) break;

          partial += decoder.decode(value, { stream: true });
          if (!isCurrentRequest(requestId)) return;

          const display = partial.replace(/^conversation_id:\s*\d+\s*/i, '');
          if (!typingRemoved && display.trim()) {
            messages.remove(typingMessage.id);
            typingRemoved = true;
          }

          if (!conversationId) {
            const conversationIdMatch = partial.match(/conversation_id:\s*(\d+)/i);
            if (conversationIdMatch) {
              conversationId = Number(conversationIdMatch[1]);
            }
          }

          messages.update(
            assistantMessage.id,
            core.normalizeResponseMarkdown(display),
          );
          await delay(typingDelayMs);
        }

        if (!typingRemoved) {
          messages.remove(typingMessage.id);
        }
      } catch (error) {
        if (isCurrentRequest(requestId) && messages.store.get(typingMessage.id)) {
          const typingWasVisible = Boolean(messages.getElement(typingMessage.id));
          messages.remove(typingMessage.id);
          if (typingWasVisible) {
            messages.append(
              messageTypes.ERROR,
              core.getChatErrorMessage(error),
              messageFormats.TEXT,
            );
          }
        }
      } finally {
        if (isCurrentRequest(requestId)) {
          client.release();
          onControlsDisabled(false);
        }
      }
    }

    return Object.freeze({
      cancel,
      get conversationId() {
        return conversationId;
      },
      send,
      setConversationId,
    });
  }

  return Object.freeze({
    createConversationController,
  });
});
